import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import DateInput from "@/components/DateInput";
import FirmSelect from "@/components/FirmSelect";
import ProjectSelect from "@/components/ProjectSelect";
import { DEFAULT_RATES, RATE_FIELDS, uid } from "@/lib/contracts";
import { CatalogPicker } from "@/components/contracts/CatalogPicker";

const EMPTY = { firm_id: "", project_id: "", contract_no: "", subject: "", contract_type: "", employer: "", contractor: "",
  amount: 0, start_date: "", end_date: "", rates: DEFAULT_RATES, items: [], advances: [] };

export function ContractForm({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [firms, setFirms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial } : EMPTY);
    api.get("/firms").then(r => setFirms(r.data));
    api.get("/projects").then(r => setProjects(r.data));
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setList = (key, i, k, v) => setF({ ...f, [key]: f[key].map((x, j) => (j === i ? { ...x, [k]: v } : x)) });

  const save = async () => {
    if (!f.firm_id || !f.project_id) { toast.error("Firma ve proje zorunlu"); return; }
    setSaving(true);
    const num = (v) => Number(v) || 0;
    const body = { ...f, amount: num(f.amount), start_date: f.start_date || null, end_date: f.end_date || null,
      rates: Object.fromEntries(Object.entries(f.rates).map(([k, v]) => [k, num(v)])),
      items: f.items.filter(i => i.name).map(i => ({ ...i, unit_price: num(i.unit_price), contract_qty: num(i.contract_qty) })),
      advances: f.advances.filter(a => num(a.amount)).map(a => ({ ...a, amount: num(a.amount), date: a.date || null })) };
    try {
      const r = initial?.id ? await api.put(`/contracts/${initial.id}`, body) : await api.post("/contracts", body);
      toast.success("Sözleşme kaydedildi"); onSaved?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="contract-form">
        <DialogHeader><DialogTitle>{initial?.id ? "Sözleşmeyi Düzenle" : "Yeni Alt Yüklenici Sözleşmesi"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Alt Yüklenici *</Label><FirmSelect firms={firms} value={f.firm_id} onChange={v => setF({ ...f, firm_id: v })} allLabel="Seçiniz" testid="cf-firm" /></div>
          <div><Label>Proje *</Label><ProjectSelect projects={projects} value={f.project_id} onChange={v => setF({ ...f, project_id: v })} includeAll={false} testid="cf-project" /></div>
          <div><Label>Sözleşme No</Label><Input value={f.contract_no} onChange={set("contract_no")} data-testid="cf-no" /></div>
          <div><Label>İşin Konusu</Label><Input value={f.subject} onChange={set("subject")} data-testid="cf-subject" /></div>
          <div><Label>Sözleşme Türü</Label><Input value={f.contract_type} onChange={set("contract_type")} placeholder="Anahtar teslim götürü bedel" /></div>
          <div><Label>Sözleşme Bedeli (KDV Hariç)</Label><Input type="number" value={f.amount} onChange={set("amount")} data-testid="cf-amount" /></div>
          <div><Label>İdare</Label><Input value={f.employer} onChange={set("employer")} /></div>
          <div><Label>Sözleşme Tarihi</Label><DateInput value={f.start_date || ""} onChange={set("start_date")} /></div>
          <div><Label>İş Bitiş Tarihi</Label><DateInput value={f.end_date || ""} onChange={set("end_date")} /></div>
        </div>
        <Section title="Oranlar">
          <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
            {RATE_FIELDS.map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label>
                <Input type="number" step="0.001" value={f.rates[k]} onChange={e => setF({ ...f, rates: { ...f.rates, [k]: e.target.value } })} data-testid={`cf-rate-${k}`} /></div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Oranlar ondalık girilir (ör. %20 = 0.2, tevkifat 4/10 = 0.4).</p>
        </Section>
        <Section title="Sözleşme Kalemleri (İş Kalemleri kataloğundan seçin veya elle ekleyin)" onAdd={() => setF({ ...f, items: [...f.items, { id: uid(), poz_no: "", name: "", unit: "", unit_price: "", contract_qty: "" }] })} testid="cf-add-item">
          <CatalogPicker onPick={(it) => {
            if (f.items.some(x => x.name === it.name && x.unit === it.unit)) { toast.info("Bu kalem zaten ekli"); return; }
            setF({ ...f, items: [...f.items, { id: uid(), poz_no: "", contract_qty: "", ...it }] }); toast.success("Katalogdan eklendi");
          }} />
          {f.items.length > 0 && <div className="flex gap-2 text-xs text-muted-foreground pr-12"><span className="w-24">Poz No</span><span className="flex-[3]">İmalatın adı</span><span className="flex-1">Birim</span><span className="flex-1">Birim fiyat</span><span className="flex-1">Sözleşme miktarı</span></div>}
          {f.items.map((it, i) => (
            <Row key={it.id} onRemove={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })}>
              <Input className="w-24" placeholder="Poz" value={it.poz_no || ""} onChange={e => setList("items", i, "poz_no", e.target.value)} data-testid={`cf-item-poz-${i}`} />
              <Input className="flex-[3]" placeholder="İmalatın adı" value={it.name} onChange={e => setList("items", i, "name", e.target.value)} data-testid={`cf-item-name-${i}`} />
              <Input className="flex-1" placeholder="Birim" value={it.unit} onChange={e => setList("items", i, "unit", e.target.value)} />
              <Input className="flex-1" type="number" placeholder="Birim fiyat" value={it.unit_price} onChange={e => setList("items", i, "unit_price", e.target.value)} data-testid={`cf-item-price-${i}`} />
              <Input className="flex-1" type="number" placeholder="Sözleşme miktarı" value={it.contract_qty} onChange={e => setList("items", i, "contract_qty", e.target.value)} />
            </Row>
          ))}
        </Section>
        <Section title="Verilen Avanslar (Nakit / Çek)" onAdd={() => setF({ ...f, advances: [...f.advances, { id: uid(), date: "", description: "", amount: "" }] })} testid="cf-add-advance">
          {f.advances.length > 0 && <div className="flex gap-2 text-xs text-muted-foreground pr-12"><span className="flex-[3]">Açıklama</span><span className="flex-1">Tutar</span></div>}
          {f.advances.map((a, i) => (
            <Row key={a.id} onRemove={() => setF({ ...f, advances: f.advances.filter((_, j) => j !== i) })}>
              <Input className="flex-[3]" placeholder="Açıklama (ör. 31/07 verilen çek)" value={a.description} onChange={e => setList("advances", i, "description", e.target.value)} />
              <Input className="flex-1" type="number" placeholder="Tutar" value={a.amount} onChange={e => setList("advances", i, "amount", e.target.value)} data-testid={`cf-adv-amount-${i}`} />
            </Row>
          ))}
        </Section>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="cf-save">{saving ? "Kaydediliyor..." : "Kaydet"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, onAdd, testid, children }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
        {onAdd && <Button size="sm" variant="outline" onClick={onAdd} data-testid={testid}><Plus className="w-4 h-4 mr-1" /> Ekle</Button>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ children, onRemove }) {
  return (
    <div className="flex gap-2 items-center">{children}
      <Button size="icon" variant="ghost" onClick={onRemove}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
    </div>
  );
}
